/**
 * Resolve the Sentry environment tag from Vercel-injected env vars first,
 * falling back to NODE_ENV for local / non-Vercel hosts.
 *
 * Why: on Vercel, NODE_ENV=production for both production AND preview
 * deployments. Without VERCEL_ENV, every misconfigured preview crash gets
 * tagged environment=production and pollutes the prod signal in Sentry.
 *
 * VERCEL_ENV values: 'production' | 'preview' | 'development'
 */
export function resolveEnvironment(): string {
  return (
    process.env.VERCEL_ENV ??
    process.env.NEXT_PUBLIC_VERCEL_ENV ??
    process.env.NODE_ENV ??
    'unknown'
  );
}

/**
 * Detect Next.js `notFound()` control-flow signals. Calling `notFound()` in a
 * layout/page/loader throws an internal error whose `digest` is
 * `NEXT_HTTP_ERROR_FALLBACK;404` (or the legacy `NEXT_NOT_FOUND`) — this is how
 * the App Router renders the 404 boundary, not a crash. `@sentry/nextjs`
 * already filters these on GET / server-component render paths, but the digest
 * LEAKS to Sentry when `notFound()` fires inside a POST server-action
 * re-render: the SDK's generic action wrapper captures the throw before its
 * notFound check runs. Symptom: BALLEE-GF / BALLEE-GG,
 * `Error: NEXT_HTTP_ERROR_FALLBACK;404` on POST /competitions/:slug/partners
 * (getCompetitionContext -> notFound() for a non-existent competition slug).
 * These are expected 404s (0 users), so drop them here to match the SDK's own
 * intent. See t5() in @sentry/nextjs's isNotFoundNavigationError.
 */
export function isNextNotFoundError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const digest = (error as { digest?: unknown }).digest;
  return (
    typeof digest === 'string' &&
    (digest === 'NEXT_HTTP_ERROR_FALLBACK;404' || digest === 'NEXT_NOT_FOUND')
  );
}
